import React, { forwardRef, memo, useEffect, useRef, useImperativeHandle } from 'react';
import type { TurnstileInstance } from '@marsidev/react-turnstile';
import { Turnstile as CloudflareTurnstile } from '@marsidev/react-turnstile';
import amplitude from 'lib/analytics';
import getTurnstileErrorMessage, { type TurnstileErrorProps } from './helpers/errorMessageHelper';

const trackChallengeReceived = (markToCompare: string, eventName: string, action: string) => {
  if (performance.getEntriesByName(markToCompare).length) {
    const turnstileChallengeSetTime = performance.measure(
      'turnstileChallengeSet',
      markToCompare,
      'turnstileChallengeSet'
    )?.duration;
    amplitude.track('Turnstile Challenge Received', {
      'Turnstile Name': action,
      'Time to Challenge Received': turnstileChallengeSetTime,
      'Challenge Generation Event': eventName
    });
    performance.clearMarks(markToCompare);
    performance.clearMarks('turnstileChallengeSet');
    performance.clearMeasures('turnstileChallengeSet');
  }
};

const setTurnstileChallenge = (action: string) => () => {
  performance.mark('turnstileChallengeSet');
  trackChallengeReceived('mount', 'Initial', action);
  trackChallengeReceived('turnstileChallengeReset', 'Reset', action);
};

interface TurnstileProps {
  action: string;
}

const Turnstile = ({ action }: TurnstileProps, ref: React.Ref<unknown>) => {
  const turnstileRef = useRef<TurnstileInstance>(null);
  const errorMessage = useRef<string>('');

  const reset = () => {
    performance.mark('turnstileChallengeReset');
    turnstileRef.current?.remove();
    turnstileRef.current?.render();
  };

  useImperativeHandle(ref, () => ({
    ...turnstileRef.current,
    reset
  }));

  useEffect(() => {
    performance.mark('mount');

    return () => {
      performance.clearMarks('mount');
      performance.clearMarks('turnstileChallengeSet');
      performance.clearMarks('turnstileChallengeReset');
      performance.clearMeasures('turnstileChallengeSet');
    };
  }, []);

  const onError = (error: TurnstileErrorProps) => {
    const errorMessageNew = error['Error Message'];
    if (errorMessageNew && errorMessage.current !== errorMessageNew) {
      errorMessage.current = errorMessageNew;
      amplitude.track('Turnstile Challenge Rejected', { ...error, 'Turnstile Name': action });
      reset();
    }
  };

  const onTurnstileScriptError = () => onError({ 'Error Message': 'Turnstile Script Error' });

  const onTurnstileError = (errorCode: string) => onError(getTurnstileErrorMessage(errorCode));

  const onTurnstileExpire = () => onError({ 'Error Message': 'Expired Challenge Error' });

  return (
    <CloudflareTurnstile
      ref={turnstileRef}
      siteKey={__ENV__.CF_TURNSTILE_SITE_KEY}
      onSuccess={setTurnstileChallenge(action)}
      options={{
        size: 'invisible',
        action
      }}
      scriptOptions={{ onError: onTurnstileScriptError }}
      onError={onTurnstileError}
      onExpire={onTurnstileExpire}
    />
  );
};

CloudflareTurnstile.displayName = 'CloudflareTurnstile';

export default memo(forwardRef(Turnstile));
